import React, {useCallback, useEffect, useRef, useState} from 'react'
import {RoleDropdownStyle} from '../ui/header'
import {Icon} from '@thryvlabs/maverick'
import {useNavigate} from 'react-router'
import {client} from '../../utils/api-client'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectEffectiveRole,
  selectUserData,
  selectUserRoles,
} from '../../app-store/selector'
import {setSwitchedRole} from '../../app-store/userRoleSlice'
import {setPartnerAC} from '../../app-store/userSlice'
import {
  SwitchRoleItem,
  SwitchRoleLabel,
  SwitchRoleList,
  SwitchRoleTrigger,
  SwitchRoleWrap,
} from '../app-shell/SwitchRole.styles'

// External store (safe — no hooks) - added, not to make changesin other files because of already using getUserRoles func
// let externalActualRole = []
// let externalSwitchedRole = []
let externalEffectiveRole = []
let externalIsSwitchRoleUser = false

// ---------------------------
// User Role Switcher Component
// ---------------------------
// `variant="redesign"` renders the app-shell look; the logic is shared so the
// external role store below stays in sync whichever UI is mounted.
export const UserRoleSwitcher = ({variant}) => {
  const navigate = useNavigate()
  const dropdownRef = useRef()
  const dispatch = useDispatch()
  const [dpOpen, setDpOpen] = useState(false)

  const {user} = useSelector(selectUserData)

  // const {actualRole, switchedRole, isSwitchRoleUser} =
  const {actualRole, isSwitchRoleUser} = useSelector(selectUserRoles)
  const effectiveRole = useSelector(selectEffectiveRole)

  const [roleOptions, setRoleOptions] = useState([])

  // ---------------------------
  // FETCH SWITCH ROLES - API: from config_lookup
  // ---------------------------
  const fetchSwitchRoles = useCallback(async () => {
    const res = await client('keappartner/fetchConfigLookupValue/switchRoles')

    const list = res?.data?.[0]?.value || ''
    return list
      .split(',')
      .map(r => r.trim())
      .filter(Boolean)
  }, [])

  useEffect(() => {
    fetchSwitchRoles().then(roles => {
      setRoleOptions(roles || [])
    })
  }, [fetchSwitchRoles])

  useEffect(() => {
    // externalActualRole = actualRole
    // externalSwitchedRole = switchedRole
    externalEffectiveRole = effectiveRole
    externalIsSwitchRoleUser = isSwitchRoleUser
  }, [isSwitchRoleUser, effectiveRole])

  // ---------------------------
  // COMBINE + REMOVE DUPLICATE ROLES
  // ---------------------------
  const combinedRoles = React.useMemo(() => {
    const a = Array.isArray(actualRole) ? actualRole : []
    const b = Array.isArray(roleOptions) ? roleOptions : []

    return [...new Set([...a, ...b])] // unique roles only
  }, [actualRole, roleOptions])

  // ---------------------------
  // CLOSE DROPDOWN ON OUTSIDE CLICK
  // ---------------------------
  useEffect(() => {
    const handler = e => {
      if (!dropdownRef.current?.contains(e.target)) {
        setDpOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ---------------------------
  // HANDLE SELECT ROLE
  // ---------------------------
  const handleSelect = role => {
    if (role === actualRole?.[0]) {
      dispatch(setSwitchedRole(null))
    } else {
      dispatch(setSwitchedRole([role]))
    }
    if (role === 'KeapPartner') {
      if (user?.network_id) {
        let pac = `pac_${user?.network_id.split('@')[0]}`
        dispatch(setPartnerAC(pac))
      }
    } else {
      dispatch(setPartnerAC(null))
    }
    setDpOpen(false)
    navigate('/')
  }

  if (variant === 'redesign') {
    return (
      <SwitchRoleWrap ref={dropdownRef}>
        <SwitchRoleLabel>Switch role</SwitchRoleLabel>
        <SwitchRoleTrigger
          type="button"
          open={dpOpen}
          aria-haspopup="listbox"
          aria-expanded={dpOpen}
          onClick={() => setDpOpen(prev => !prev)}
        >
          {effectiveRole?.[0] || 'Select role'}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
          >
            <path
              d="M2.5 4.5 6 8l3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SwitchRoleTrigger>
        {dpOpen && (
          <SwitchRoleList role="listbox">
            {combinedRoles.map(role => (
              <SwitchRoleItem
                key={role}
                role="option"
                active={effectiveRole?.[0] === role}
                aria-selected={effectiveRole?.[0] === role}
                onClick={() => handleSelect(role)}
              >
                {role}
              </SwitchRoleItem>
            ))}
          </SwitchRoleList>
        )}
      </SwitchRoleWrap>
    )
  }

  return (
    <RoleDropdownStyle>
      <div className="role-dropdown" ref={dropdownRef}>
        <div className="role-dp-btn-wrap">
          <span className="role-dp-btn-label">Role: </span>

          <span
            className="role-dp-trigger"
            onClick={() => setDpOpen(prev => !prev)}
          >
            {effectiveRole?.[0] || 'Switch Role'}
            {/* {actualRole?.[0] || 'Switch Role'} */}

            <Icon
              style={{
                cursor: 'pointer',
                width: '15px',
              }}
              type="solid"
              color="#a9a9a9"
              variant={!dpOpen ? 'caretDown' : 'caretUp'}
              className="ml-2"
            />
          </span>
        </div>

        {dpOpen && (
          <ul className="role-dp-menu">
            {combinedRoles.map(role => (
              <li
                key={role}
                className={`role-dp-item ${
                  actualRole === role ? 'active' : ''
                }`}
                onClick={() => handleSelect(role)}
              >
                {role}
              </li>
            ))}
          </ul>
        )}
      </div>
    </RoleDropdownStyle>
  )
}

// safe getters for utilities :- get-user-roles (can NOT use hooks, because this utility already used in many other components, to avoide creating a getter function)
export function getSwitchedRoleStore() {
  return {
    // actualRole: externalActualRole,
    // switchedRole: externalSwitchedRole,
    effectiveRole: externalEffectiveRole,
    isSwitchRoleUser: externalIsSwitchRoleUser,
  }
}
